import React, { createContext, useState, useEffect, useMemo, useCallback } from 'react';
import en from '../locales/en.json';
import pt from '../locales/pt.json';

const LocaleContext = createContext({
  locale: 'en',
  setLocale: () => {},
  t: (key) => key,
  translations: {},
});

const allTranslations = { en, pt };
const STORAGE_KEY = 'locale';

function resolveKey(obj, keyPath) {
  if (!obj) return undefined;
  if (!keyPath) return undefined;
  // support dotted paths and top-level arrays
  const parts = keyPath.split('.');
  let cur = obj;
  for (let p of parts) {
    if (cur === undefined || cur === null) return undefined;
    cur = cur[p];
  }
  return cur;
}

export const LocaleProvider = ({ children }) => {
  const [locale, setLocaleState] = useState('en');

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setLocaleState(stored);
    } else {
      // auto-detect on first visit
      const nav = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : 'en';
      if (nav && nav.toLowerCase().startsWith('pt')) setLocaleState('pt');
      else setLocaleState('en');
    }
  }, []);

  const setLocale = useCallback((l) => {
    setLocaleState(l);
    try { localStorage.setItem(STORAGE_KEY, l); } catch (e) {}
  }, []);

  const t = useCallback((key, fallback) => {
    // if key is falsy, return empty string
    if (!key) return '';
    const val = resolveKey(allTranslations[locale], key);
    if (val !== undefined) return val;
    // fallback to English
    const enVal = resolveKey(allTranslations['en'], key);
    if (enVal !== undefined) return enVal;
    return fallback ?? key;
  }, [locale]);

  const value = useMemo(() => ({ locale, setLocale, t, translations: allTranslations }), [locale, setLocale, t]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
};

export default LocaleContext;
